import { useMemo } from "react";
import environment from "src/utils/environment";
import time from "src/utils/time";
import useAnimationFrameUpdater from "src/hooks/useAnimationFrameUpdater";
import SvgContainer from "src/components/SvgContainer";
import { Activities } from "src/modules/activity-log/activity";
import { ReactComponent as MusicIcon } from "src/modules/lullaby/icons/music-outlined.svg";
import { ReactComponent as LoopIcon } from "src/modules/lullaby/icons/loop.svg";
import Container from "./components/Container";
import Text from "./components/Text";

export default function LullabyReplayIndicator({
  lullabyPlayer,
  openLullabyPlayer,
  isDarkMode,
  error,
  activity,
  mode,
  ...rest
}) {
  useAnimationFrameUpdater({ updateRate: 2 });
  let { bg, hoverBg } = useMemo(
    () => ({
      bg:
        isDarkMode &&
        (activity === Activities.SLEEPING ||
          (activity === Activities.MOTION && environment.isBarkio)) &&
        mode === "audio"
          ? "white20"
          : "black8",
      hoverBg:
        isDarkMode &&
        (activity === Activities.SLEEPING ||
          (activity === Activities.MOTION && environment.isBarkio)) &&
        mode === "audio"
          ? "white40"
          : "black20"
    }),
    [activity, isDarkMode, mode]
  );

  let color = useMemo(
    () =>
      !isDarkMode &&
      (activity === Activities.SLEEPING ||
        (activity === Activities.MOTION && environment.isBarkio)) &&
      mode === "audio"
        ? "black54"
        : "white",
    [isDarkMode, activity, mode]
  );

  if (error) {
    bg = "black8";
    hoverBg = "black8";
    color = "white";
  }

  const { timerSettings } = lullabyPlayer;
  const lullaby = lullabyPlayer.getLullaby();
  const looped = !timerSettings.enabled && lullaby.looped;
  const duration = timerSettings.enabled
    ? timerSettings.duration
    : lullaby.duration;
  const remainingTime = duration - lullabyPlayer.getCurrentTime();

  const underHourMinWidth = ["36px", "43px", "53px"];
  const aboveHourMinWidth = ["51px", "67px", "83px"];

  return (
    <Container
      p={["6px 12px", "8px 16px"]}
      bg={bg}
      hoverBg={hoverBg}
      onClick={openLullabyPlayer}
      {...rest}
    >
      <SvgContainer fill={color} size={["16px", "20px"]} icon={MusicIcon} />
      {looped ? (
        <SvgContainer
          icon={LoopIcon}
          size={["16px", "20px"]}
          ml={["4px", "6px", "8px"]}
          fill={color}
        />
      ) : (
        <Text
          textAlign="center"
          color={color}
          fontSize={["12px", "16px", "20px"]}
          minWidth={
            remainingTime >= 60 * 60 * 1000
              ? aboveHourMinWidth
              : underHourMinWidth
          }
          ml={["4px", "6px", "8px"]}
          lineHeight="1"
        >
          {time
            .duration(remainingTime >= 0 ? remainingTime : 0)
            .format("h:mm:ss", { stopTrim: "m" })}
        </Text>
      )}
    </Container>
  );
}
